import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Camera, Compass, Eye, Thermometer, Radio, Settings, Zap, TrendingUp, Droplet, Cpu } from 'lucide-react';
import { ArrowRight } from 'lucide-react';

export const ServicesPage: React.FC = () => {
  return (
    <main className="min-h-screen bg-white text-zinc-900 overflow-x-hidden">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-96 flex items-center justify-center px-6 py-32 mt-16 bg-gradient-to-br from-blue-600 to-cyan-500">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.h1
            className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Professional Drone Services
          </motion.h1>
          <motion.p
            className="text-xl text-gray-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Industry-leading solutions for every aerial challenge
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="space-y-20">
            {/* Aerial Photography & Videography */}
            <motion.div
              className="grid md:grid-cols-2 gap-12 items-center"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Camera className="w-8 h-8 text-blue-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-zinc-900">Aerial Photography & Videography</h2>
                </div>
                <p className="text-lg text-zinc-700 mb-6">
                  Capture stunning aerial visuals from perspectives that tell your story. Our expert pilots and advanced camera systems deliver broadcast-quality content for marketing, real estate, events, and media production.
                </p>
                <ul className="space-y-3">
                  {['4K & 8K Video Capture', 'Professional Color Grading', 'Cinematic Motion Control', 'Multi-Angle Coverage', 'Live Event Streaming', 'Time-lapse & Panoramic Shots'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-zinc-700">
                      <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl h-80 flex items-center justify-center">
                <Camera className="w-32 h-32 text-blue-400 opacity-50" />
              </div>
            </motion.div>

            {/* Mapping & Surveying */}
            <motion.div
              className="grid md:grid-cols-2 gap-12 items-center"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl h-80 flex items-center justify-center md:order-2">
                <Compass className="w-32 h-32 text-green-400 opacity-50" />
              </div>
              <div className="md:order-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Compass className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-zinc-900">Mapping & Surveying</h2>
                </div>
                <p className="text-lg text-zinc-700 mb-6">
                  Precision geospatial data collection using RTK-GPS and advanced sensors. Perfect for construction planning, land surveys, volumetric analysis, and environmental monitoring.
                </p>
                <ul className="space-y-3">
                  {['RTK-GPS Accuracy', '3D Site Mapping', 'Volumetric Measurements', 'Orthomosaic Creation', 'Digital Elevation Models', 'Construction Progress Tracking'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-zinc-700">
                      <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Infrastructure Inspection */}
            <motion.div
              className="grid md:grid-cols-2 gap-12 items-center"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <Eye className="w-8 h-8 text-orange-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-zinc-900">Infrastructure Inspection</h2>
                </div>
                <p className="text-lg text-zinc-700 mb-6">
                  Safe, efficient inspection of buildings, bridges, power lines, wind turbines, and renewable installations without risk or costly equipment. Detailed visual assessments and structural analysis.
                </p>
                <ul className="space-y-3">
                  {['Close-range Detail Photography', 'Structural Assessment Reports', 'Damage Documentation', 'Progress Monitoring', 'Difficult-to-Access Areas', 'Safety Compliance Verification'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-zinc-700">
                      <span className="w-2 h-2 bg-orange-600 rounded-full"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-orange-100 to-red-100 rounded-xl h-80 flex items-center justify-center">
                <Eye className="w-32 h-32 text-orange-400 opacity-50" />
              </div>
            </motion.div>

            {/* Thermal Imaging */}
            <motion.div
              className="grid md:grid-cols-2 gap-12 items-center"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl h-80 flex items-center justify-center md:order-2 overflow-hidden">
                <img
                  src="/assets/services-thermal-imaging.jpg"
                  alt="Thermal imaging analysis"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="md:order-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <Thermometer className="w-8 h-8 text-purple-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-zinc-900">Thermal Imaging & Analysis</h2>
                </div>
                <p className="text-lg text-zinc-700 mb-6">
                  Advanced thermal sensing technology for diagnostics, building energy efficiency assessment, electrical system analysis, and quality assurance across multiple industries.
                </p>
                <ul className="space-y-3">
                  {['Radiometric Thermal Data', 'Building Energy Loss Detection', 'Electrical System Diagnostics', 'Heat Distribution Analysis', 'Night Operations Capability', 'Precision Temperature Mapping'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-zinc-700">
                      <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Search & Rescue Support */}
            <motion.div
              className="grid md:grid-cols-2 gap-12 items-center"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <Radio className="w-8 h-8 text-red-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-zinc-900">Search & Rescue Support</h2>
                </div>
                <p className="text-lg text-zinc-700 mb-6">
                  Rapid response capabilities with advanced thermal and optical sensors for emergency operations. Real-time coordination with rescue teams for maximum effectiveness.
                </p>
                <ul className="space-y-3">
                  {['24/7 Response Readiness', 'Advanced Thermal Detection', 'Real-time Data Streaming', 'Large Area Coverage', 'Night Operations Capability', 'Coordination Integration'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-zinc-700">
                      <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-red-100 to-orange-100 rounded-xl h-80 flex items-center justify-center overflow-hidden">
                <img
                  src="/assets/services-search-rescue.jpg"
                  alt="Search and Rescue drone operations"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            {/* Specialized Services */}
            <motion.div
              className="grid md:grid-cols-2 gap-12 items-center"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl h-80 flex items-center justify-center md:order-2">
                <Settings className="w-32 h-32 text-indigo-400 opacity-50" />
              </div>
              <div className="md:order-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-indigo-100 rounded-lg">
                    <Settings className="w-8 h-8 text-indigo-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-zinc-900">Custom Solutions & Specialized Operations</h2>
                </div>
                <p className="text-lg text-zinc-700 mb-6">
                  Tailored drone operations designed for your unique requirements. From payload integration to complex mission planning, we handle specialized applications with precision.
                </p>
                <ul className="space-y-3">
                  {['Custom Payload Integration', 'Complex Mission Planning', 'Precision Delivery Operations', 'Environmental Monitoring', 'Agricultural Analysis', 'Research & Development Support'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-zinc-700">
                      <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Features */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-zinc-900 mb-4">What's Included in Every Service</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Zap,
                title: 'Expert Pilots',
                description: 'Certified professionals with extensive operational experience'
              },
              {
                icon: TrendingUp,
                title: 'Detailed Reporting',
                description: 'Comprehensive analysis and actionable recommendations'
              },
              {
                icon: Cpu,
                title: 'Advanced Technology',
                description: 'State-of-the-art equipment and latest sensor systems'
              },
              {
                icon: Droplet,
                title: 'Weather Monitoring',
                description: 'Real-time environmental assessment for safety'
              },
              {
                icon: Radio,
                title: 'Live Coordination',
                description: 'Direct communication and real-time data streaming'
              },
              {
                icon: Settings,
                title: 'Post-Processing',
                description: 'Professional editing, analysis, and delivery'
              },
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                className="p-6 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <feature.icon className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="font-bold text-zinc-900 mb-2">{feature.title}</h3>
                <p className="text-zinc-600 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-blue-600">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Get Started?</h2>
            <p className="text-xl text-gray-100 mb-8">
              Contact us for a free consultation to discuss which service best fits your needs.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 hover:scale-105 active:scale-95 transition-all duration-200"
            >
              Schedule Free Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
};
